import reverse from 'lodash/reverse';
import sortBy from 'lodash/sortBy';
import { storableError } from '../../util/errors';
import { parse } from '../../util/urlHelpers';
import { TRANSITIONS } from '../../util/transaction';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { updateTransactionMetadata } from '../../util/api';
import { createImageVariantConfig } from '../../util/sdkLoader';
import config from '../../config';

const sortedTransactions = txs =>
  reverse(
    sortBy(txs, tx => {
      return tx.attributes ? tx.attributes.lastTransitionedAt : null;
    })
  );

// ================ Action types ================ //

export const FETCH_ORDERS_OR_SALES_REQUEST = 'app/InboxPage/FETCH_ORDERS_OR_SALES_REQUEST';
export const FETCH_ORDERS_OR_SALES_SUCCESS = 'app/InboxPage/FETCH_ORDERS_OR_SALES_SUCCESS';
export const FETCH_ORDERS_OR_SALES_ERROR = 'app/InboxPage/FETCH_ORDERS_OR_SALES_ERROR';

export const UPADTE_METADATA_REQUEST = 'app/InboxPage/UPADTE_METADATA_REQUEST';
export const UPADTE_METADATA_SUCCESS = 'app/InboxPage/UPADTE_METADATA_SUCCESS';

export const FETCH_TRANSACTIONS_SUCCESS = 'app/InboxPage/FETCH_TRANSACTIONS_SUCCESS';

// ================ Reducer ================ //

const entityRefs = entities =>
  entities.map(entity => ({
    id: entity.id,
    type: entity.type,
  }));

const initialState = {
  fetchInProgress: false,
  fetchOrdersOrSalesError: null,
  pagination: null,
  transactionRefs: [],
  updateInProgress: null,

  transactionCountRefs: [],
};

export default function checkoutPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_ORDERS_OR_SALES_REQUEST:
      return { ...state, fetchInProgress: true, fetchOrdersOrSalesError: null };
    case FETCH_ORDERS_OR_SALES_SUCCESS: {
      const transactions = sortedTransactions(payload.data.data);
      return {
        ...state,
        fetchInProgress: false,
        transactionRefs: entityRefs(transactions),
        pagination: payload.data.meta,
      };
    }
    case FETCH_ORDERS_OR_SALES_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, fetchInProgress: false, fetchOrdersOrSalesError: payload };

    case UPADTE_METADATA_REQUEST:
      return { ...state, updateInProgress: payload };
    case UPADTE_METADATA_SUCCESS: {
      return {
        ...state,
        updateInProgress: null,
        // pagination: payload.data.meta,
      };
    }

    case FETCH_TRANSACTIONS_SUCCESS:
      const transactions = sortedTransactions(payload.data.data);
      return {
        ...state,
        // fetchInProgress: false,
        transactionCountRefs: entityRefs(transactions),
      };

    default:
      return state;
  }
}

// ================ Action creators ================ //

const fetchOrdersOrSalesRequest = () => ({ type: FETCH_ORDERS_OR_SALES_REQUEST });
const fetchOrdersOrSalesSuccess = response => ({
  type: FETCH_ORDERS_OR_SALES_SUCCESS,
  payload: response,
});
const fetchOrdersOrSalesError = e => ({
  type: FETCH_ORDERS_OR_SALES_ERROR,
  error: true,
  payload: e,
});

const updateMetaDataRequest = data => ({ type: UPADTE_METADATA_REQUEST, payload: data });
const updateMetaDataSuccess = response => ({
  type: UPADTE_METADATA_SUCCESS,
  payload: response,
});

export const fetchTransactionsSuccess = data => ({
  type: FETCH_TRANSACTIONS_SUCCESS,
  payload: data,
});

// ================ Thunks ================ //

const INBOX_PAGE_SIZE = 40;

export const updatedMetaData = data => (dispatch, getState, sdk) => {
  console.log(data, '((( ))) => data');

  dispatch(updateMetaDataRequest(data.id));
  try {
    updateTransactionMetadata({ ...data })
      .then(res => {
        if (res) {
          return sdk.transactions.query().then(response => {
            dispatch(addMarketplaceEntities(response));
            dispatch(updateMetaDataSuccess(response));
          });
        }
        return res;
      })
      .catch(error => {
        console.error('Error querying transactions:', error);
      });
  } catch (error) {
    console.error('Error updating metadata:', error);
  }
};

export const fetchAllTransactionsData = (params, search) => (dispatch, getState, sdk) => {
  const { tab } = params;

  const onlyFilterValues = {
    orders: 'order',
    sales: 'sale',
  };

  const onlyFilter = onlyFilterValues[tab];
  if (!onlyFilter) {
    return Promise.reject(new Error(`Invalid tab for InboxPage: ${tab}`));
  }

  dispatch(fetchOrdersOrSalesRequest());

  const { page = 1 } = parse(search);

  const { aspectWidth = 1, aspectHeight = 1, variantPrefix = 'listing-card' } = config.listing;
  const aspectRatio = aspectHeight / aspectWidth;
  const apiQueryParams = {
    only: onlyFilter,
    lastTransitions: TRANSITIONS,
    include: [
      'listing',
      'provider',
      'provider.profileImage',
      'customer',
      'customer.profileImage',
      'booking',
      'listing.images',
    ],
    'fields.transaction': [
      'lastTransition',
      'lastTransitionedAt',
      'transitions',
      'payinTotal',
      'payoutTotal',
      'lineItems',
      'metadata',
    ],
    'fields.listing': ['title', 'geolocation', 'price', 'publicData'],
    'fields.user': ['profile.displayName', 'profile.abbreviatedName'],
    'fields.image': [`variants.${variantPrefix}`, `variants.${variantPrefix}-2x`],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    'limit.images': 1,
    page,
    per_page: INBOX_PAGE_SIZE,
  };
  return sdk.transactions
    .query(apiQueryParams)
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      dispatch(fetchOrdersOrSalesSuccess(response));
      return response;
    })
    .catch(e => {
      dispatch(fetchOrdersOrSalesError(storableError(e)));
      throw e;
    });
};

export const fetchAllTransactionsCountData = (params, search) => (dispatch, getState, sdk) => {
  const { page = 1 } = parse(search);

  const { aspectWidth = 1, aspectHeight = 1, variantPrefix = 'listing-card' } = config.listing;
  const aspectRatio = aspectHeight / aspectWidth;

  const apiQueryParams = {
    lastTransitions: TRANSITIONS,
    include: [
      'listing',
      'provider',
      'provider.profileImage',
      'customer',
      'customer.profileImage',
      'booking',
      'listing.images',
    ],
    'fields.transaction': [
      'lastTransition',
      'lastTransitionedAt',
      'transitions',
      'payinTotal',
      'payoutTotal',
      'lineItems',
      'metadata',
    ],
    'fields.listing': ['title', 'geolocation', 'price', 'publicData'],
    'fields.user': ['profile.displayName', 'profile.abbreviatedName'],
    'fields.image': [`variants.${variantPrefix}`, `variants.${variantPrefix}-2x`],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    'limit.images': 1,
    page,
    per_page: INBOX_PAGE_SIZE,
  };

  return sdk.transactions
    .query(apiQueryParams)
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      dispatch(fetchTransactionsSuccess(response));
      return response;
    })
    .catch(e => {
      throw e;
    });
};

export const loadData = (params, search) => (dispatch, getState, sdk) => {
  return Promise.all([
    dispatch(fetchAllTransactionsCountData(params, search)),
    dispatch(fetchAllTransactionsData(params, search)),
  ]);
};
